import axios from 'axios';
import {
  LoginCredentials,
  RegisterData,
  AuthTokens,
  User,
  TwoFactorData,
  ChangePasswordData,
  LoginResponse,
  TrustedDevice,
} from '../types/auth';
import { getDeviceFingerprint } from '../utils/deviceFingerprint';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

class AuthService {
  private baseURL = API_BASE_URL;

  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    const fingerprint = await getDeviceFingerprint();

    const formData = new FormData();
    formData.append('username', credentials.email);
    formData.append('password', credentials.password);

    const response = await axios.post(`${this.baseURL}/auth/login`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'X-Device-Fingerprint': fingerprint,
      },
    });

    return response.data;
  }

  async verifyTwoFactorLogin(sessionId: string, totpToken: string): Promise<LoginResponse> {
    const fingerprint = await getDeviceFingerprint();

    const response = await axios.post(`${this.baseURL}/auth/verify-2fa`, {
      session_id: sessionId,
      totp_token: totpToken,
      device_fingerprint: fingerprint,
      user_agent: navigator.userAgent,
    });

    return response.data;
  }

  async verifyBackupCode(sessionId: string, backupCode: string): Promise<LoginResponse> {
    const fingerprint = await getDeviceFingerprint();

    const response = await axios.post(`${this.baseURL}/auth/verify-backup-code`, {
      session_id: sessionId,
      totp_token: backupCode,
      device_fingerprint: fingerprint,
      user_agent: navigator.userAgent,
    });

    return response.data;
  }

  async register(userData: RegisterData): Promise<AuthTokens> {
    const response = await axios.post(`${this.baseURL}/auth/register`, userData);
    return response.data;
  }

  async refreshToken(refreshToken: string): Promise<AuthTokens> {
    const response = await axios.post(`${this.baseURL}/auth/refresh`, {
      refresh_token: refreshToken,
    });
    return response.data;
  }

  async getCurrentUser(token: string): Promise<User> {
    const response = await axios.get(`${this.baseURL}/users/me`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  }

  async enable2FA(token: string): Promise<{
    secret: string;
    qr_code: string;
    backup_codes: string[];
  }> {
    const response = await axios.post(`${this.baseURL}/auth/2fa/enable`, {}, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  }

  async verify2FA(token: string, twoFactorData: TwoFactorData): Promise<{ message: string }> {
    const response = await axios.post(`${this.baseURL}/auth/2fa/verify`, twoFactorData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  }

  async disable2FA(token: string, twoFactorData: TwoFactorData): Promise<{ message: string }> {
    const response = await axios.post(`${this.baseURL}/auth/2fa/disable`, twoFactorData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  }

  async changePassword(token: string, passwordData: ChangePasswordData): Promise<{ message: string }> {
    const response = await axios.post(`${this.baseURL}/auth/change-password`, passwordData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  }

  async getDevices(token: string): Promise<TrustedDevice[]> {
    const fingerprint = await getDeviceFingerprint();
    const response = await axios.get(`${this.baseURL}/auth/devices`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Device-Fingerprint': fingerprint,
      },
    });
    return response.data;
  }

  async removeDevice(token: string, deviceId: number): Promise<void> {
    await axios.delete(`${this.baseURL}/auth/devices/${deviceId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  // Token management — single JSON format
  getStoredTokens(): AuthTokens | null {
    const tokensJson = localStorage.getItem('authTokens');
    if (tokensJson) {
      try { return JSON.parse(tokensJson); } catch {}
    }
    return null;
  }

  /** Get the current access token string, or null if not logged in. */
  getAccessToken(): string | null {
    const tokens = this.getStoredTokens();
    return tokens?.access_token ?? null;
  }

  storeTokens(tokens: AuthTokens): void {
    localStorage.setItem('authTokens', JSON.stringify(tokens));
  }

  removeTokens(): void {
    localStorage.removeItem('authTokens');
  }

  isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp * 1000 < Date.now();
    } catch {
      return true; // Treat malformed tokens as expired
    }
  }
}

export default new AuthService();
